import { FC } from 'react';
import { Box, Text, VStack, HStack, Badge } from '@chakra-ui/react';
import { AIPromptHistory } from '@/types/aiManagement';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

interface VersionHistoryProps {
  histories: AIPromptHistory[];
  currentVersion: number;
  onSelectVersion?: (history: AIPromptHistory) => void;
  maxItems?: number;
}

/**
 * プロンプトのバージョン履歴表示コンポーネント
 */
export const VersionHistory: FC<VersionHistoryProps> = ({
  histories,
  currentVersion,
  onSelectVersion,
  maxItems = 5,
}) => {
  const displayHistories = histories.slice(0, maxItems);
  const hasMore = histories.length > maxItems;

  if (histories.length === 0) {
    return (
      <Box p={4} bg="gray.50" borderRadius="md">
        <Text fontSize="sm" color="gray.500" textAlign="center">
          バージョン履歴はありません
        </Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" gap={2}>
      {displayHistories.map((history) => (
        <Box
          key={history.id}
          p={3}
          bg="white"
          borderWidth={1}
          borderRadius="md"
          cursor={onSelectVersion ? 'pointer' : 'default'}
          transition="all 0.2s"
          _hover={
            onSelectVersion
              ? { bg: 'gray.50', borderColor: 'blue.300' }
              : undefined
          }
          onClick={() => onSelectVersion?.(history)}
        >
          <HStack justify="space-between" mb={1}>
            <HStack gap={2}>
              <Badge
                colorPalette={
                  history.version === currentVersion ? 'blue' : 'gray'
                }
                fontSize="xs"
              >
                v{history.version}
              </Badge>
              {history.version === currentVersion && (
                <Badge colorPalette="green" fontSize="xs">
                  現在
                </Badge>
              )}
            </HStack>
            <Text fontSize="xs" color="gray.500">
              {format(history.createdAt.toDate(), 'yyyy/MM/dd HH:mm', {
                locale: ja,
              })}
            </Text>
          </HStack>
          {history.changeNote && (
            <Text fontSize="sm" color="gray.600" mt={1}>
              {history.changeNote}
            </Text>
          )}
          {!history.changeNote && (
            <Text fontSize="sm" color="gray.400" mt={1} fontStyle="italic">
              変更理由なし
            </Text>
          )}
        </Box>
      ))}

      {hasMore && (
        <Text
          fontSize="sm"
          color="blue.500"
          textAlign="center"
          cursor="pointer"
          _hover={{ textDecoration: 'underline' }}
        >
          さらに {histories.length - maxItems} 件の履歴を表示
        </Text>
      )}
    </VStack>
  );
};

interface VersionCompareProps {
  oldVersion: AIPromptHistory;
  newVersion: AIPromptHistory;
}

/**
 * 2つのバージョンを比較表示
 */
export const VersionCompare: FC<VersionCompareProps> = ({
  oldVersion,
  newVersion,
}) => {
  const oldWeights = oldVersion.snapshot.weights;
  const newWeights = newVersion.snapshot.weights;

  const weightChanges = [
    {
      label: '遺伝的多様性',
      old: oldWeights.geneticDiversity,
      new: newWeights.geneticDiversity,
    },
    {
      label: '繁殖実績',
      old: oldWeights.breedingHistory,
      new: newWeights.breedingHistory,
    },
    {
      label: '特徴マッチング',
      old: oldWeights.traitMatch,
      new: newWeights.traitMatch,
    },
  ];

  const oldThresholds = oldVersion.snapshot.inbreedingThresholds;
  const newThresholds = newVersion.snapshot.inbreedingThresholds;

  const thresholdChanges = [
    { label: '安全閾値', old: oldThresholds.safe, new: newThresholds.safe },
    { label: '注意閾値', old: oldThresholds.caution, new: newThresholds.caution },
    { label: '警告閾値', old: oldThresholds.warning, new: newThresholds.warning },
  ];

  return (
    <VStack align="stretch" gap={4}>
      <HStack justify="space-between">
        <Badge colorPalette="gray">v{oldVersion.version}</Badge>
        <Text color="gray.400">→</Text>
        <Badge colorPalette="blue">v{newVersion.version}</Badge>
      </HStack>

      {/* 重み付け変更 */}
      <Box>
        <Text fontWeight="medium" fontSize="sm" mb={2}>
          重み付けの変更
        </Text>
        <VStack align="stretch" gap={1}>
          {weightChanges
            .filter((change) => change.old !== change.new)
            .map((change) => (
              <HStack key={change.label} justify="space-between" fontSize="sm">
                <Text color="gray.600">{change.label}</Text>
                <HStack>
                  <Text color="red.500">{change.old}%</Text>
                  <Text color="gray.400">→</Text>
                  <Text color="green.500">{change.new}%</Text>
                </HStack>
              </HStack>
            ))}
          {weightChanges.every((change) => change.old === change.new) && (
            <Text fontSize="sm" color="gray.400">
              変更なし
            </Text>
          )}
        </VStack>
      </Box>

      {/* 閾値変更 */}
      <Box>
        <Text fontWeight="medium" fontSize="sm" mb={2}>
          閾値の変更
        </Text>
        <VStack align="stretch" gap={1}>
          {thresholdChanges
            .filter((change) => change.old !== change.new)
            .map((change) => (
              <HStack key={change.label} justify="space-between" fontSize="sm">
                <Text color="gray.600">{change.label}</Text>
                <HStack>
                  <Text color="red.500">{change.old}%</Text>
                  <Text color="gray.400">→</Text>
                  <Text color="green.500">{change.new}%</Text>
                </HStack>
              </HStack>
            ))}
          {thresholdChanges.every((change) => change.old === change.new) && (
            <Text fontSize="sm" color="gray.400">
              変更なし
            </Text>
          )}
        </VStack>
      </Box>
    </VStack>
  );
};
